/* ============================================================================
   EVERENZA — global interaction layer
   Implements §11: overlays (cart drawer, filter drawer, size guide, zoom,
   mobile nav), accordions, toast, sticky ATC, PDP selectors + gallery retint,
   and the AJAX cart engine (Shopify Section Rendering API).

   Public API (window.EVERENZA):
     openOverlay(id) / closeOverlay(id) / closeAllOverlays()
     toast(message)
     cartAdd(formEl)         -> Promise
     refreshCart()           -> Promise (re-render drawer + counts)
   Markup contract:
     Overlay element:  id="<id>"  class="...ev-overlay"  + matching scrim
     Opener:           [data-ev-open="<id>"]
     Closer:           [data-ev-close]  (closes nearest open overlay)
     Accordion:        .ev-acc  with  [data-ev-acc-toggle]  trigger
     Cart count badge: [data-ev-cart-count]
     Add-to-cart form: [data-ev-cart-form]
     Drawer host:      #ev-cart-drawer-host  (re-rendered from section 'ev-cart-drawer')
     Qty / remove:     [data-ev-cart-change] with data-line + data-qty
     Sticky ATC:       real button [data-ev-atc] ; sticky bar [data-ev-sticky-atc]
     PDP root:         [data-ev-pdp]
   ============================================================================ */
(function () {
  'use strict';

  var BODY = document.body;
  var openCount = 0;
  var _lockScrollY = 0;

  /* ---------- scroll lock ----------
     iOS Safari ignores `overflow:hidden` on <body> for touch scrolling, so the page
     scrolled behind overlays. Pin the body with position:fixed and restore the scroll
     position on release. Only toggles on the first open / last close. */
  function lockScroll() {
    openCount++;
    if (openCount !== 1) return;
    _lockScrollY = window.scrollY || window.pageYOffset || 0;
    BODY.style.position = 'fixed';
    BODY.style.top = (-_lockScrollY) + 'px';
    BODY.style.left = '0';
    BODY.style.right = '0';
    BODY.style.width = '100%';
    BODY.style.overflow = 'hidden';
  }
  function unlockScroll() {
    openCount = Math.max(0, openCount - 1);
    if (openCount !== 0) return;
    BODY.style.position = '';
    BODY.style.top = '';
    BODY.style.left = '';
    BODY.style.right = '';
    BODY.style.width = '';
    BODY.style.overflow = '';
    window.scrollTo(0, _lockScrollY);
  }

  /* ---------- overlays ---------- */
  function scrimFor(id) { return document.querySelector('[data-ev-scrim="' + id + '"]'); }

  var _openers = {};
  function focusablesIn(el) {
    return Array.prototype.filter.call(
      el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null; }
    );
  }
  function setOpenerExpanded(id, open) {
    document.querySelectorAll('[data-ev-open="' + id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', open ? 'true' : 'false'); });
  }
  function openOverlay(id) {
    var el = document.getElementById(id);
    if (!el || el.classList.contains('is-open')) return;
    _openers[id] = document.activeElement; // remember the trigger to restore focus on close
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    var s = scrimFor(id); if (s) s.classList.add('is-open');
    lockScroll();
    setOpenerExpanded(id, true);
    var f = focusablesIn(el);
    var first = el.querySelector('[autofocus]') || f[0];
    if (first) { try { first.focus({ preventScroll: true }); } catch (e) {} }
  }
  function closeOverlay(id) {
    var el = document.getElementById(id);
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    var s = scrimFor(id); if (s) s.classList.remove('is-open');
    unlockScroll();
    setOpenerExpanded(id, false);
    var opener = _openers[id];
    if (opener && opener.focus && document.body.contains(opener)) { try { opener.focus({ preventScroll: true }); } catch (e) {} }
    _openers[id] = null;
  }
  function closeAllOverlays() {
    document.querySelectorAll('.ev-overlay.is-open').forEach(function (el) { closeOverlay(el.id); });
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-ev-open]');
    if (opener) { e.preventDefault(); openOverlay(opener.getAttribute('data-ev-open')); return; }

    var closer = e.target.closest('[data-ev-close]');
    if (closer) {
      // A [data-ev-close] that is ALSO a real link (e.g. cart "Choose options", nav-drawer
      // collection links) must still NAVIGATE — only preventDefault for non-link closers (✕ buttons).
      var _href = closer.getAttribute('href');
      var _isLink = closer.tagName === 'A' && _href && _href.charAt(0) !== '#';
      if (!_isLink) e.preventDefault();
      var explicit = closer.getAttribute('data-ev-close');
      if (explicit) { closeOverlay(explicit); }
      else { var ov = closer.closest('.ev-overlay'); if (ov) closeOverlay(ov.id); }
      return;
    }
    var scrim = e.target.closest('[data-ev-scrim]');
    if (scrim) { closeOverlay(scrim.getAttribute('data-ev-scrim')); return; }

    // backdrop click on a centered, full-viewport overlay (zoom, size guide):
    // clicking the overlay's own padding (not its inner panel) closes it.
    if (e.target.classList && e.target.classList.contains('ev-overlay') && e.target.classList.contains('is-open')) {
      closeOverlay(e.target.id);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var open = document.querySelectorAll('.ev-overlay.is-open');
      if (open.length) closeOverlay(open[open.length - 1].id);
      return;
    }
    // focus trap: keep Tab within the topmost open overlay
    if (e.key === 'Tab') {
      var overlays = document.querySelectorAll('.ev-overlay.is-open');
      if (!overlays.length) return;
      var panel = overlays[overlays.length - 1];
      var f = focusablesIn(panel);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- accordions (§11.2) ---------- */
  document.addEventListener('click', function (e) {
    var trig = e.target.closest('[data-ev-acc-toggle]');
    if (!trig) return;
    var acc = trig.closest('.ev-acc');
    if (!acc) return;
    var willOpen = !acc.classList.contains('is-open');
    if (acc.hasAttribute('data-ev-acc-exclusive')) {
      var group = acc.parentElement.querySelectorAll(':scope > .ev-acc[data-ev-acc-exclusive]');
      group.forEach(function (a) { a.classList.remove('is-open'); var t = a.querySelector('[data-ev-acc-toggle]'); if (t) t.setAttribute('aria-expanded', 'false'); });
    }
    acc.classList.toggle('is-open', willOpen);
    trig.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  });

  /* ---------- toast (§11.5) ---------- */
  var toastEl = null, toastTimer = null;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ev-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      BODY.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    // force reflow so the transition replays
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 2400);
  }

  /* ---------- analytics (§12): GA4 dataLayer + Meta pixel, no-op if absent ---------- */
  function track(gaEvent, payload, fbEvent, fbPayload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: gaEvent }, payload || {}));
    } catch (e) {}
    try { if (window.fbq && fbEvent) window.fbq('track', fbEvent, fbPayload || {}); } catch (e) {}
  }

  /* ---------- cart engine (Section Rendering API) ---------- */
  var DRAWER_SECTION = 'ev-cart-drawer';
  var DRAWER_HOST = 'ev-cart-drawer-host';

  function getSectionsUrl() { return window.location.pathname; }

  function renderDrawerFromSections(sections) {
    if (!sections) return;
    var host = document.getElementById(DRAWER_HOST);
    var html = sections[DRAWER_SECTION];
    if (host && typeof html === 'string') {
      var drawer = document.getElementById('ev-cart-drawer');
      var wasOpen = drawer && drawer.classList.contains('is-open');
      host.innerHTML = html;
      if (wasOpen) {
        var fresh = document.getElementById('ev-cart-drawer');
        if (fresh) { fresh.classList.add('is-open'); fresh.setAttribute('aria-hidden', 'false'); }
        var s = scrimFor('ev-cart-drawer'); if (s) s.classList.add('is-open');
      }
    }
  }

  function updateCounts(count) {
    document.querySelectorAll('[data-ev-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.toggleAttribute('hidden', count === 0);
    });
  }

  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCounts(cart.item_count);
        return fetch('/?sections=' + DRAWER_SECTION, { headers: { 'Accept': 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (sections) { renderDrawerFromSections(sections); })
      .catch(function () {});
  }


  // Thin top progress bar (FeelinGirl-style): fills while the add is in flight so the
  // drawer can open only once it's populated — no empty flash.
  var _progEl = null, _progTmr = null;
  function cartProgress(state) {
    if (state === 'start') {
      if (!_progEl) { _progEl = document.createElement('div'); _progEl.className = 'ev-cart-progress'; document.body.appendChild(_progEl); }
      clearTimeout(_progTmr);
      _progEl.classList.remove('is-active'); _progEl.style.width = '0';
      void _progEl.offsetWidth; // reflow so the next width change animates from 0
      _progEl.classList.add('is-active'); _progEl.style.width = '80%';
    } else {
      if (!_progEl) return;
      _progEl.style.width = '100%';
      _progTmr = setTimeout(function () { if (_progEl) { _progEl.classList.remove('is-active'); _progEl.style.width = '0'; } }, 320);
    }
  }

  // Lazy video posters: <video data-ev-lazyposter="url"> ships with no poster so the browser's
  // preload scanner can't fetch it on load. We set .poster only when the element nears the
  // viewport (IntersectionObserver) + a scroll fallback (IO can be throttled in some contexts).
  var _lazyPosterBound = false;
  function initLazyPosters() {
    var pending = function () { return document.querySelectorAll('video[data-ev-lazyposter]'); };
    var load = function (v) {
      var url = v.getAttribute('data-ev-lazyposter');
      if (!url) return;
      v.setAttribute('poster', url);
      v.removeAttribute('data-ev-lazyposter');
    };
    var vids = pending();
    if (!vids.length) return;
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (ents) {
        ents.forEach(function (e) { if (e.isIntersecting) { load(e.target); io.unobserve(e.target); } });
      }, { rootMargin: '600px 0px' });
      Array.prototype.forEach.call(vids, function (v) { io.observe(v); });
    }
    // scroll/resize fallback (also covers IO-throttled automation tabs); throttled by timestamp
    if (!_lazyPosterBound) {
      _lazyPosterBound = true;
      var _last = 0;
      var scan = function () {
        var now = Date.now(); if (now - _last < 150) return; _last = now;
        var vv = pending(); if (!vv.length) return;
        var h = window.innerHeight || 800;
        Array.prototype.forEach.call(vv, function (v) {
          var r = v.getBoundingClientRect();
          if (r.top < h + 600 && r.bottom > -600) load(v);
        });
      };
      window.addEventListener('scroll', scan, { passive: true });
      window.addEventListener('resize', scan, { passive: true });
      scan(); // load any that are already near on first paint
    }
  }

  var _addBusy = false;
  function cartAdd(form) {
    if (_addBusy) return Promise.resolve(); // ignore double-clicks / re-entry while an add is in flight (prevents duplicate line items)
    _addBusy = true;
    var data = new FormData(form);
    // Ask /cart/add.js to also return the re-rendered drawer (Section Rendering API) so we
    // can populate the drawer BEFORE opening it — no empty flash, no second round-trip.
    data.append('sections', DRAWER_SECTION);
    data.append('sections_url', getSectionsUrl());
    var btn = form.querySelector('[type="submit"], [data-ev-atc]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    // Track whether the drawer was ALREADY open (re-add while drawer is showing) so a
    // failed add doesn't wipe out the user's already-open drawer — only undo the
    // optimistic open we performed ourselves.
    var _drawerEl = document.getElementById('ev-cart-drawer');
    var wasOpen = !!(_drawerEl && _drawerEl.classList.contains('is-open'));
    cartProgress('start'); // show the bar; open the drawer only AFTER it's populated (no empty flash)
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; }, function () { return { ok: r.ok, status: r.status, body: null, parseError: true }; }); })
      .then(function (res) {
        if (!res.ok || !res.body) {
          var msg = (res.body && (res.body.description || res.body.message)) || 'Could not add to cart';
          console.warn('[EVERENZA] cart/add.js failed', { status: res.status, body: res.body, postedId: (data.get && data.get('id')) });
          toast(msg);
          return;
        }
        // populate the drawer FIRST, then open it — so it never flashes empty
        try { renderDrawerFromSections(res.body.sections); } catch (e) {}
        if (!wasOpen) openOverlay('ev-cart-drawer');
        toast('Added to your cart');
        var added = (res.body.items && res.body.items[0]) || res.body;
        track('add_to_cart', {
          ecommerce: { items: [{ item_id: added.product_id, item_name: added.product_title || added.title, price: added.final_price ? added.final_price / 100 : undefined, quantity: added.quantity || 1 }] }
        }, 'AddToCart', { content_name: added.product_title || added.title });
        // refresh just the header count badge (drawer is already rendered → no re-render flicker)
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (c) { updateCounts(c.item_count); })
          .catch(function () {});
      })
      .catch(function (err) { console.error('[EVERENZA] cart/add.js network/error', err); toast('Could not add to cart'); })
      .finally(function () { if (btn) btn.removeAttribute('aria-busy'); cartProgress('done'); _addBusy = false; });
  }

  var _cartBusy = false;
  function cartChange(line, qty, focusLine) {
    if (_cartBusy) return Promise.resolve(); // drop overlapping qty clicks so updates aren't lost
    _cartBusy = true;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty, sections: DRAWER_SECTION, sections_url: getSectionsUrl() })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCounts(cart.item_count);
        renderDrawerFromSections(cart.sections);
        document.dispatchEvent(new CustomEvent('everenza:cart:changed', { detail: cart }));
        // restore keyboard focus after the drawer re-render (innerHTML detaches the clicked control)
        if (focusLine != null) {
          var d = document.getElementById('ev-cart-drawer');
          if (d) {
            var same = d.querySelector('[data-ev-cart-change][data-line="' + focusLine + '"]');
            var target = same || d.querySelector('[data-ev-close], button, a, [tabindex]');
            if (target) { try { target.focus({ preventScroll: true }); } catch (e) {} }
          }
        }
      })
      .catch(function () { toast('Could not update your cart — please try again.'); })
      .finally(function () { _cartBusy = false; });
  }

  // When the Kaching Bundles app is on the page it OWNS add-to-cart: it has its own per-item
  // colour/size selectors and applies the bundle discount via its own /cart/add. Defer to it —
  // don't gate on the theme size selector and don't run the theme's single-variant cartAdd.
  function kachingActive() { return !!document.querySelector('.kaching-bundles__block, .kaching-bundles__bars'); }

  // intercept add-to-cart forms — ALWAYS AJAX, even on Kaching pages. Kaching's
  // intercept_cart_request catches our /cart/add.js and expands it into the full bundle
  // (discount + free items) with NO page redirect, and cartAdd opens our populated drawer.
  // (Deferring to the native form submit was what caused the redirect to /cart.)
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-ev-cart-form]');
    if (!form) return;
    e.preventDefault();
    cartAdd(form);
  });

  /* Remove several lines at once — used by the bundle row in the drawer.
     A Kaching bundle is several cart lines, so removing it one line at a time would
     reshuffle the indices between calls and delete the wrong rows. /cart/update.js
     keyed by LINE ITEM KEY is index-independent, so the whole group goes in one call. */
  function cartRemoveKeys(keys) {
    if (_cartBusy) return Promise.resolve();
    if (!keys || !keys.length) return Promise.resolve();
    _cartBusy = true;
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: DRAWER_SECTION, sections_url: getSectionsUrl() })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCounts(cart.item_count);
        renderDrawerFromSections(cart.sections);
        document.dispatchEvent(new CustomEvent('everenza:cart:changed', { detail: cart }));
        var d = document.getElementById('ev-cart-drawer');
        if (d) {
          var target = d.querySelector('[data-ev-close], button, a, [tabindex]');
          if (target) { try { target.focus({ preventScroll: true }); } catch (e) {} }
        }
      })
      .catch(function () { toast('Could not update your cart — please try again.'); })
      .finally(function () { _cartBusy = false; });
  }

  // qty steppers + remove inside the drawer (and any [data-ev-cart-change])
  document.addEventListener('click', function (e) {
    var ctrl = e.target.closest('[data-ev-cart-change]');
    if (!ctrl) return;
    e.preventDefault();
    var keys = ctrl.getAttribute('data-keys');
    if (keys) {
      cartRemoveKeys(keys.split(',').filter(Boolean));
      return;
    }
    var line = parseInt(ctrl.getAttribute('data-line'), 10);
    var qty = parseInt(ctrl.getAttribute('data-qty'), 10);
    if (!isNaN(line)) cartChange(line, isNaN(qty) ? 0 : qty, line);
  });

  /* ---------- sticky ATC (§11.1) ---------- */
  var _stickyTick = false;
  function rafSticky() {
    if (_stickyTick) return;
    _stickyTick = true;
    requestAnimationFrame(function () { _stickyTick = false; updateSticky(); });
  }
  function updateSticky() {
    var atc = document.querySelector('[data-ev-atc]');
    var sticky = document.querySelector('[data-ev-sticky-atc]');
    if (!atc || !sticky) return;
    // Anchor the bar to the SIZE BLOCK, not to the ATC button.
    //
    // The original rule waited until the real ATC had scrolled off the top. On mobile
    // that button sits ~2540px down (3.1 screens on a 375x812 viewport), so a shopper
    // arriving from an ad had no way to buy for three full screens. The rule existed
    // to stop the bar covering the price on load, and anchoring to the size block keeps
    // that guarantee: the price sits above the selectors, so by the time the size block
    // has scrolled away the price is already gone too.
    //
    // Falls back to the ATC when a product has no size option.
    var anchor = document.querySelector('[data-ev-size-block]') || atc;
    var r = anchor.getBoundingClientRect();
    var passed = r.bottom <= 10;
    // Second guard, 2026-08-19: never show the bar while the REAL button is on screen.
    // With Kaching mounted, the bundle block sits between the size block and the ATC,
    // so the size block scrolls off while the button is still fully visible — and the
    // shopper saw two Add-to-cart buttons stacked. The size-block anchor still decides
    // the EARLIEST the bar may appear; this decides that it must wait for the real
    // button to leave the viewport first. A small tolerance below the fold so the bar
    // appears as the button exits, not a screen later.
    var ar = atc.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var atcOnScreen = ar.bottom > 0 && ar.top < vh - 8;
    if (atcOnScreen) passed = false;
    sticky.classList.toggle('is-visible', passed);
    // keep a11y state in lockstep so SR users aren't blocked when it's shown and
    // its button isn't a focusable off-screen trap when it's hidden
    sticky.setAttribute('aria-hidden', passed ? 'false' : 'true');
    var sBtn = sticky.querySelector('[data-ev-sticky-action]');
    if (sBtn) { if (passed) { sBtn.removeAttribute('tabindex'); } else { sBtn.setAttribute('tabindex', '-1'); } }
  }
  function initStickyAtc() {
    var sticky = document.querySelector('[data-ev-sticky-atc]');
    if (!sticky) return;
    // position:fixed only tracks the viewport when no ancestor has a transform/
    // filter/contain (Dawn section wrappers + scroll-reveal animations can add one,
    // which would pin the bar to the section instead of the screen). Relocating the
    // bar to <body> guarantees it stays fixed to the viewport. De-dupe stale copies
    // left by a previous section render.
    if (sticky.parentElement !== document.body) {
      document.querySelectorAll('body > [data-ev-sticky-atc]').forEach(function (n) {
        if (n !== sticky) n.remove();
      });
      document.body.appendChild(sticky);
    }
    updateSticky();
    if (window.__evStickyBound) return;
    window.__evStickyBound = true;
    // capture phase so it fires even if a scroll container intercepts; passive for perf
    window.addEventListener('scroll', rafSticky, { passive: true, capture: true });
    window.addEventListener('resize', rafSticky);
    window.addEventListener('load', updateSticky);
  }

  /* ---------- PDP controller ---------- */
  function initPdp() {
    var root = document.querySelector('[data-ev-pdp]');
    if (!root) return;
    if (root.dataset.evPdpBound) return; // avoid double-binding on shopify:section:load re-renders
    root.dataset.evPdpBound = '1';

    // ---- gallery video layer (over the main image) ----
    var galMainVideo = root.querySelector('[data-ev-main-video]');
    var galMainImg = root.querySelector('[data-ev-main-image]');
    function evStopVideoSlot(s) {
      if (!s) return;
      var v = s.querySelector('video'); if (v) { try { v.pause(); } catch (e) {} }
      var f = s.querySelector('iframe'); if (f) { var sc = f.getAttribute('src'); if (sc) { f.setAttribute('src', sc); } } // reload stops external playback
    }
    function evShowImageView() {
      if (galMainVideo) {
        galMainVideo.querySelectorAll('[data-ev-vslot]').forEach(function (s) { if (!s.hidden) evStopVideoSlot(s); s.hidden = true; });
        galMainVideo.hidden = true;
      }
      if (galMainImg) galMainImg.style.display = '';
    }
    if (galMainVideo) galMainVideo.addEventListener('click', function (e) { e.stopPropagation(); }); // don't open image zoom

    // ----- desktop dual-sticky columns: each gets position:sticky + a dynamic top so the
    // shorter column pins at the top while the taller scrolls, then pins its bottom — works
    // whichever column is longer (and recomputes when the colour changes the gallery height). -----
    var _galCol = root.querySelector('.ev-pdp__gallery');
    var _buyCol = root.querySelector('.ev-pdp__buybox');
    function setStickyCols() {
      if (!_galCol || !_buyCol) return;
      if (window.innerWidth < 769) {
        _galCol.style.position = ''; _galCol.style.top = '';
        _buyCol.style.position = ''; _buyCol.style.top = '';
        return;
      }
      var vh = window.innerHeight;
      [_galCol, _buyCol].forEach(function (col) {
        col.style.position = 'sticky';
        var h = col.offsetHeight;
        col.style.top = (h <= vh - 40) ? '20px' : (vh - h - 20) + 'px';
      });
    }
    var _stickyTmr;
    window.addEventListener('resize', function () { clearTimeout(_stickyTmr); _stickyTmr = setTimeout(setStickyCols, 120); });
    window.addEventListener('load', setStickyCols);
    // Recompute sticky offsets when the buy-box/gallery height changes (e.g. an in-column
    // accordion expands) so the lower buy-box content never gets clipped out of reach.
    if (window.ResizeObserver && _buyCol) {
      var _ro = new ResizeObserver(function () { clearTimeout(_stickyTmr); _stickyTmr = setTimeout(setStickyCols, 60); });
      _ro.observe(_buyCol);
      if (_galCol) _ro.observe(_galCol);
    }

    var variants = readJson(root.querySelector('[data-ev-variants]'));
    var stockMap = readJson(root.querySelector('[data-ev-stock]')) || {}; // { variantId: 'in' | 'low' | 'out' }
    var grads = readJson(root.querySelector('[data-ev-gallery-grads]')) || {};
    var optionMap = readJson(root.querySelector('[data-ev-option-map]')) || {}; // { color: position, size: position }
    var priceFmt = root.getAttribute('data-ev-price-format') || '${{amount}}';

    var state = { Color: null, Size: null };
    var hasSize = !!root.querySelector('[data-ev-size]');
    var hasColor = !!root.querySelector('[data-ev-color]');
    // seed defaults from active swatch / nothing for size
    var activeColor = root.querySelector('[data-ev-color].is-active');
    if (activeColor) state.Color = activeColor.getAttribute('data-ev-color');

    function findVariant() {
      if (!Array.isArray(variants)) return null;
      return variants.find(function (v) {
        // option1/option2/option3 are guaranteed in `product.variants | json`
        // (the `options` array is not always present). Match by 1-based position.
        var vc = optionMap.color ? (v['option' + optionMap.color] || (v.options && v.options[optionMap.color - 1])) : null;
        var vs = optionMap.size ? (v['option' + optionMap.size] || (v.options && v.options[optionMap.size - 1])) : null;
        var okC = !optionMap.color || !state.Color || vc === state.Color;
        var okS = !optionMap.size || !state.Size || vs === state.Size;
        return okC && okS;
      }) || null;
    }

    // Perf: thumbs/tiles for non-active colours ship WITHOUT an <img> (only data-ev-*img on
    // the button). Build the <img> for a colour the first time it's shown, from those attrs.
    function hydrateColor(color) {
      // mobile thumbs
      root.querySelectorAll('[data-ev-thumb][data-ev-media-color]').forEach(function (b) {
        var mc = b.getAttribute('data-ev-media-color');
        if ((mc === color || !mc) && !b.querySelector('img')) {
          var src = b.getAttribute('data-ev-thumbimg'); if (!src) return;
          var img = document.createElement('img');
          img.src = src; img.alt = ''; img.loading = 'lazy'; img.width = 165; img.height = 220;
          b.insertBefore(img, b.firstChild);
        }
      });
      // desktop tiles
      root.querySelectorAll('[data-ev-galtile][data-ev-media-color]').forEach(function (b) {
        var mc = b.getAttribute('data-ev-media-color');
        if ((mc === color || !mc) && !b.querySelector('img')) {
          var src = b.getAttribute('data-ev-tileimg'); if (!src) return;
          var img = document.createElement('img');
          img.src = src; img.alt = ''; img.loading = 'lazy'; img.width = 560; img.height = 700;
          var ss = b.getAttribute('data-ev-tilesrcset'); if (ss) img.srcset = ss;
          img.sizes = '(min-width:769px) 26vw, 50vw';
          b.insertBefore(img, b.firstChild);
        }
      });
    }
    // Fallback: build an <img> for EVERY gallery button (used when a colour has no tagged
    // media so the gallery would otherwise be empty — e.g. alt-tags drifted after a re-import).
    function hydrateAll() {
      root.querySelectorAll('[data-ev-thumb]').forEach(function (b) {
        if (b.querySelector('img')) return;
        var s = b.getAttribute('data-ev-thumbimg'); if (!s) return;
        var img = document.createElement('img');
        img.src = s; img.alt = ''; img.loading = 'lazy'; img.width = 165; img.height = 220;
        b.insertBefore(img, b.firstChild);
      });
      root.querySelectorAll('[data-ev-galtile]').forEach(function (b) {
        if (b.querySelector('img')) return;
        var s = b.getAttribute('data-ev-tileimg'); if (!s) return;
        var img = document.createElement('img');
        img.src = s; img.alt = ''; img.loading = 'lazy'; img.width = 560; img.height = 700;
        var ss = b.getAttribute('data-ev-tilesrcset'); if (ss) img.srcset = ss;
        img.sizes = '(min-width:769px) 26vw, 50vw';
        b.insertBefore(img, b.firstChild);
      });
    }
    // desktop image grid: show only the selected colour's tiles (plus untagged media)
    function filterGrid() {
      var tiles = root.querySelectorAll('[data-ev-galtile][data-ev-media-color]');
      if (!tiles.length) return;
      tiles.forEach(function (t) {
        var mc = t.getAttribute('data-ev-media-color');
        t.style.display = (!state.Color || !mc || mc === state.Color) ? '' : 'none';
      });
    }
    function retint() {
      var g = grads[state.Color];
      if (g) root.style.setProperty('--ev-gallery-grad', g);
      if (state.Color) hydrateColor(state.Color); // build this colour's images before showing them
      filterGrid();
      // sticky-column reflow (reads offsetHeight) is deferred off the click→paint path so it
      // doesn't add to INP; the ResizeObserver also recomputes it when the gallery resizes.
      setTimeout(setStickyCols, 0);
      // Per-colour gallery: show only the selected colour's thumbnails (plus any
      // untagged media), and set the main image to the first visible one.
      var thumbs = root.querySelectorAll('[data-ev-thumb][data-ev-media-color]');
      if (!thumbs.length || !state.Color) return;
      var matchExists = false;
      thumbs.forEach(function (t) { if (t.getAttribute('data-ev-media-color') === state.Color) matchExists = true; });
      if (!matchExists) return; // this colour has no tagged media — leave the gallery as-is
      var firstVisible = null;
      thumbs.forEach(function (t) {
        var mc = t.getAttribute('data-ev-media-color');
        var show = !mc || mc === state.Color; // untagged media stay visible for all colours
        t.style.display = show ? '' : 'none';
        if (show && !firstVisible) firstVisible = t;
      });
      if (firstVisible) {
        evShowImageView(); // a colour change dismisses any open gallery video
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === firstVisible); });
        var mainImg = root.querySelector('[data-ev-main-image]');
        var src = firstVisible.getAttribute('data-image');
        if (mainImg && src) {
          var fvss = firstVisible.getAttribute('data-srcset');
          if (fvss) { mainImg.srcset = fvss; } else { mainImg.removeAttribute('srcset'); }
          mainImg.src = src;
        }
        var mainTag = root.querySelector('[data-ev-main-tag]');
        if (mainTag && firstVisible.dataset.tag) mainTag.textContent = firstVisible.dataset.tag;
        var mainCap = root.querySelector('[data-ev-main-caption]');
        if (mainCap && firstVisible.dataset.caption) mainCap.textContent = firstVisible.dataset.caption;
        root._zoomTag = firstVisible.dataset.tag;
        root._zoomCap = firstVisible.dataset.caption;
      }
    }

    function updateAtc() {
      var atc = root.querySelector('[data-ev-atc]');
      var idInput = root.querySelector('[data-ev-variant-id]');
      var v = findVariant();
      var sizeChosen = !hasSize || !!state.Size;
      // only commit the variant id + variant-derived price once the selection is COMPLETE
      // (every present option chosen) — a colour-only match returns the first size's variant
      // and would otherwise show its price and pre-fill its id before the user picks a size.
      var complete = (!hasColor || !!state.Color) && (!hasSize || !!state.Size);
      if (idInput && v && complete) idInput.value = v.id;

      // price (update when a COMPLETE variant is matched)
      var priceEl = root.querySelector('[data-ev-price]');
      if (priceEl && v && complete) priceEl.textContent = formatMoney(v.price, priceFmt);
      var stickyPriceEl = document.querySelector('[data-ev-sticky-price]');
      if (stickyPriceEl && v && complete) stickyPriceEl.textContent = formatMoney(v.price, priceFmt);

      // savings tag (Rule of 100) — recompute from the selected variant's compare_at_price
      if (v && complete && priceEl) {
        var cmp = v.compare_at_price || 0;
        var onSale = cmp > v.price;
        var compareEl = root.querySelector('[data-ev-compare]');
        var saveEl = root.querySelector('[data-ev-save]');
        priceEl.classList.toggle('ev-pdp__amount--sale', onSale);
        if (compareEl) {
          if (onSale) { compareEl.textContent = formatMoney(cmp, priceFmt); compareEl.removeAttribute('hidden'); }
          else { compareEl.setAttribute('hidden', ''); }
        }
        if (saveEl) {
          if (onSale) {
            var saveAmt = cmp - v.price;
            var savePct = Math.round(saveAmt * 100 / cmp);
            var pctLabel = savePct + '% OFF';
            var amtLabel = 'Save ' + formatMoney(saveAmt, priceFmt);
            var leadsPct = (cmp / 100) < 100; // Rule of 100: under $100 → % leads, else $ leads
            var pEl = saveEl.querySelector('[data-ev-save-primary]');
            if (pEl) pEl.textContent = leadsPct ? pctLabel : amtLabel;
            saveEl.removeAttribute('hidden');
          } else {
            saveEl.setAttribute('hidden', '');
          }
        }
      }

      if (!atc) return;
      var label = atc.querySelector('[data-ev-atc-label]') || atc;
      // Kaching bundles present → keep the button clickable + clear (it carries its own sizes); the theme doesn't gate.
      if (kachingActive()) {
        atc.dataset.evState = 'ready';
        label.textContent = 'Add to cart';
        atc.disabled = false;
        atc.removeAttribute('aria-disabled');
        syncSticky(label.textContent, 'ready');
        updateAvail();
        return;
      }
      // Live availability: a product (or one-size / colour-only product) with no available
      // variant at all must surface as sold out rather than a clickable Add-to-cart.
      var anyAvailable = Array.isArray(variants) && variants.some(function (vv) { return vv.available; });
      // Spec §6.2.13 — sold-out size buttons are disabled and can't be selected; the live
      // /cart/add.js call is still the source of truth on click.
      if (!anyAvailable || (complete && v && v.available === false)) {
        atc.dataset.evState = 'soldout';
        label.textContent = 'Sold out';
        atc.disabled = true;
        atc.setAttribute('aria-disabled', 'true');
      } else if (!sizeChosen) {
        atc.dataset.evState = 'choose';
        label.textContent = 'Choose your size';
        atc.disabled = false;
        atc.removeAttribute('aria-disabled');
      } else if (complete && !v) {
        // the chosen colour+size combination has no variant — block, don't post a stale id
        atc.dataset.evState = 'unavailable';
        label.textContent = 'Unavailable in this combination';
        atc.disabled = true;
        atc.setAttribute('aria-disabled', 'true');
      } else {
        atc.dataset.evState = 'ready';
        var pm = v ? formatMoney(v.price, priceFmt) : (root.getAttribute('data-ev-base-price-formatted') || '');
        label.textContent = 'Add to cart — ' + pm;
        atc.disabled = false;
        atc.removeAttribute('aria-disabled');
      }
      syncSticky(label.textContent, atc.dataset.evState);
      updateAvail();
    }

    // Honest availability line (above the ATC): in stock / low stock / back in stock soon.
    function updateAvail() {
      var line = root.querySelector('[data-ev-avail]');
      if (!line) return;
      var txt = line.querySelector('[data-ev-avail-text]');
      var v = findVariant();
      var complete = (!hasColor || !!state.Color) && (!hasSize || !!state.Size);
      var anyAvailable = Array.isArray(variants) && variants.some(function (vv) { return vv.available; });
      var st, msg;
      if (!anyAvailable) {
        st = 'out'; msg = 'Back in stock soon';
      } else if (!complete) {
        st = 'in'; msg = 'In stock · Ships in 1–3 business days';
      } else if (!v || v.available === false) {
        st = 'out'; msg = 'Back in stock soon';
      } else {
        var s = stockMap[String(v.id)] || 'in';
        if (s === 'low') {
          st = 'low';
          msg = state.Size ? ('Low stock in ' + state.Size + ' — only a few left') : 'Low stock — only a few left';
        } else if (s === 'out') {
          st = 'out'; msg = 'Back in stock soon';
        } else {
          st = 'in'; msg = 'In stock · Ships in 1–3 business days';
        }
      }
      line.dataset.evState = st;
      if (txt) txt.textContent = msg;
    }

    function syncSticky(label, st) {
      var s = document.querySelector('[data-ev-sticky-atc]');
      if (!s) return;
      var sl = s.querySelector('[data-ev-atc-label]');
      if (sl) sl.textContent = label;
      var sbtn = s.querySelector('[data-ev-sticky-action]');
      if (sbtn && st) sbtn.dataset.evState = st;
      var meta = s.querySelector('[data-ev-sticky-meta]');
      if (meta) {
        var parts = [];
        if (state.Color) parts.push(state.Color);
        parts.push(state.Size ? 'Size ' + state.Size : 'Select size');
        parts.push('First exchange free');
        meta.textContent = parts.join(' · ');
      }
    }

    // Re-strike the size buttons for the colour that is actually selected.
    // Liquid renders the first colour's availability; every colour change has to redo it,
    // otherwise a size that exists in some OTHER colour stays clickable and she picks a
    // combination that does not exist. Keep this in step with the sold-out loop in
    // sections/everenza-product.liquid.
    function syncSizes() {
      if (!hasSize || !variants.length) return;
      var si = optionMap.size ? optionMap.size - 1 : -1;
      var ci = optionMap.color ? optionMap.color - 1 : -1;
      if (si < 0) return;
      var activeStillOk = false;
      root.querySelectorAll('[data-ev-size]').forEach(function (btn) {
        var val = btn.getAttribute('data-ev-size');
        var ok = variants.some(function (v) {
          if (!v.available) return false;
          if (v.options[si] !== val) return false;
          if (ci >= 0 && state.Color && v.options[ci] !== state.Color) return false;
          return true;
        });
        btn.classList.toggle('is-soldout', !ok);
        btn.disabled = !ok;
        if (ok) {
          btn.removeAttribute('data-ev-soldout');
          btn.removeAttribute('aria-disabled');
        } else {
          btn.setAttribute('data-ev-soldout', '');
          btn.setAttribute('aria-disabled', 'true');
        }
        if (ok && state.Size === val) activeStillOk = true;
      });
      // the size she had chosen may not exist in the new colour — drop it rather than
      // leaving a selected-looking button that cannot be added to cart
      if (state.Size && !activeStillOk) {
        state.Size = '';
        root.querySelectorAll('[data-ev-size]').forEach(function (x) {
          x.classList.remove('is-active');
          x.setAttribute('aria-pressed', 'false');
        });
        var sl = root.querySelector('[data-ev-size-label]');
        if (sl) sl.textContent = 'Select';
      }
    }

    // colour swatches
    root.querySelectorAll('[data-ev-color]').forEach(function (sw) {
      sw.addEventListener('click', function () {
        state.Color = sw.getAttribute('data-ev-color');
        root.querySelectorAll('[data-ev-color]').forEach(function (x) { var on = x === sw; x.classList.toggle('is-active', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        var lbl = root.querySelector('[data-ev-color-label]');
        if (lbl) lbl.textContent = state.Color;
        syncSizes();
        retint();
        updateAtc();
      });
    });

    // size buttons — bind them all. A button that is sold out in the current colour can
    // become available in the next one, so the guard has to be at click time, not bind time.
    root.querySelectorAll('[data-ev-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.hasAttribute('data-ev-soldout')) return;
        state.Size = btn.getAttribute('data-ev-size');
        root.querySelectorAll('[data-ev-size]').forEach(function (x) { var on = x === btn; x.classList.toggle('is-active', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        var lbl = root.querySelector('[data-ev-size-label]');
        if (lbl) lbl.textContent = state.Size;
        updateAtc();
      });
    });

    syncSizes();

    // gallery thumbnails (placeholder + real)
    var mainTag = root.querySelector('[data-ev-main-tag]');
    var mainCap = root.querySelector('[data-ev-main-caption]');
    var mainImg = root.querySelector('[data-ev-main-image]');
    root.querySelectorAll('[data-ev-thumb]').forEach(function (th) {
      th.addEventListener('click', function () {
        root.querySelectorAll('[data-ev-thumb]').forEach(function (x) { x.classList.toggle('is-active', x === th); });
        if (mainTag && th.dataset.tag) mainTag.textContent = th.dataset.tag;
        if (mainCap && th.dataset.caption) mainCap.textContent = th.dataset.caption;
        var vid = th.getAttribute('data-ev-video-id');
        if (vid && galMainVideo) {
          if (mainImg) mainImg.style.display = 'none';
          galMainVideo.hidden = false;
          galMainVideo.querySelectorAll('[data-ev-vslot]').forEach(function (s) {
            var match = s.getAttribute('data-ev-vslot') === vid;
            if (!match && !s.hidden) evStopVideoSlot(s);
            s.hidden = !match;
          });
        } else {
          evShowImageView();
          var src = th.getAttribute('data-image');
          if (mainImg && src) { mainImg.src = src; mainImg.srcset = th.getAttribute('data-srcset') || ''; }
        }
        if (mainTag) { root._zoomTag = th.dataset.tag; }
        if (mainCap) { root._zoomCap = th.dataset.caption; }
      });
      // warm the full-size image on hover/touch so the swap is instant (no lag)
      var _pre;
      function _preloadThumb() {
        if (_pre) return;
        var u = th.getAttribute('data-image');
        if (!u) return;
        _pre = new Image();
        _pre.sizes = '(min-width:769px) 50vw, 100vw';
        _pre.srcset = th.getAttribute('data-srcset') || '';
        _pre.src = u;
      }
      th.addEventListener('pointerenter', _preloadThumb);
      th.addEventListener('touchstart', _preloadThumb, { passive: true });
    });

    // (removed) idle preload of EVERY gallery image — on a 96-image product that downloaded
    // ~14MB + decoded 96 images, saturating bandwidth (so ATC waited) and janking the UI.
    // The cheap per-thumb hover/touch preload above is enough.

    // UGC mini strip (under ATC): show a RANDOM 4 of the full set (per page load)
    var _ugcMini = root.querySelector('.ev-pdp__ugcmini');
    if (_ugcMini) {
      var _ugcCards = Array.prototype.slice.call(_ugcMini.querySelectorAll('.ev-pdp__ugcminicard'));
      if (_ugcCards.length > 4) {
        for (var _i = _ugcCards.length - 1; _i > 0; _i--) {
          var _j = Math.floor(Math.random() * (_i + 1));
          var _t = _ugcCards[_i]; _ugcCards[_i] = _ugcCards[_j]; _ugcCards[_j] = _t;
        }
        _ugcCards.slice(4).forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); });
      }
    }

    // UGC mini strip (under ATC): hide the play overlay while a clip is playing
    root.querySelectorAll('.ev-pdp__ugcminicard video').forEach(function (uv) {
      var ucard = uv.closest('.ev-pdp__ugcminicard');
      if (!ucard) return;
      uv.addEventListener('play', function () { ucard.classList.add('is-playing'); });
      uv.addEventListener('pause', function () { ucard.classList.remove('is-playing'); });
      uv.addEventListener('ended', function () { ucard.classList.remove('is-playing'); });
    });

    // Perf: UGC video posters (both rails) ship WITHOUT a poster attr; set it only when the
    // rail nears the viewport so ~180 KiB of below-fold posters don't starve the LCP image on load.
    initLazyPosters();

    // gallery prev/next — page through the currently visible thumbnails
    function stepGallery(dir) {
      var thumbs = Array.prototype.filter.call(
        root.querySelectorAll('[data-ev-thumb]'),
        function (t) { return t.offsetParent !== null && t.style.display !== 'none'; }
      );
      if (thumbs.length < 2) return;
      var cur = 0;
      for (var i = 0; i < thumbs.length; i++) { if (thumbs[i].classList.contains('is-active')) { cur = i; break; } }
      var ni = cur + dir;
      if (ni < 0) ni = thumbs.length - 1;
      if (ni >= thumbs.length) ni = 0;
      var target = thumbs[ni];
      target.click(); // reuse the thumb handler (updates main image + active state)
      try { target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' }); } catch (e) {}
    }
    var galPrev = root.querySelector('[data-ev-gallery-prev]');
    var galNext = root.querySelector('[data-ev-gallery-next]');
    if (galPrev) galPrev.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); stepGallery(-1); });
    if (galNext) galNext.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); stepGallery(1); });
    var mainBox = root.querySelector('.ev-pdp__main');
    if (mainBox) {
      mainBox.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); stepGallery(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); stepGallery(1); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); mainBox.click(); } // open zoom
      });
      // touch swipe (mobile): left -> next, right -> prev; vertical gestures stay page-scroll,
      // and a swipe suppresses the tap-to-zoom that would otherwise fire on touchend.
      var _swX = 0, _swY = 0, _swipe = false;
      mainBox.addEventListener('touchstart', function (e) {
        var t = e.touches && e.touches[0]; if (!t) return;
        _swX = t.clientX; _swY = t.clientY; _swipe = false;
      }, { passive: true });
      mainBox.addEventListener('touchmove', function (e) {
        var t = e.touches && e.touches[0]; if (!t) return;
        var dx = t.clientX - _swX, dy = t.clientY - _swY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 12) { _swipe = true; }
      }, { passive: true });
      mainBox.addEventListener('touchend', function (e) {
        if (!_swipe) return;
        var t = e.changedTouches && e.changedTouches[0];
        var dx = t ? t.clientX - _swX : 0;
        if (Math.abs(dx) > 40) { root._galSwiped = true; stepGallery(dx < 0 ? 1 : -1); }
      }, { passive: true });
    }

    // ATC submit guard: require a size, and block a doomed add when the
    // selected colour+size variant is sold out / unavailable.
    var form = root.querySelector('[data-ev-cart-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        if (kachingActive()) return; // Kaching bundles owns the add — don't block on the theme size selector
        // Only hard-block when a size is required but not chosen. Availability is
        // validated live by /cart/add.js (never trust possibly-stale embedded data,
        // which would falsely block in-stock items).
        if (hasSize && !state.Size) {
          e.preventDefault();
          e.stopImmediatePropagation();
          toast('Please choose your size');
          var sizeBlock = root.querySelector('[data-ev-size-block]');
          if (sizeBlock) sizeBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        // block when the fully-chosen colour+size combination resolves to no variant
        // (e.g. that size exists in another colour but not this one) — never post a stale id
        var complete2 = (!hasColor || !!state.Color) && (!hasSize || !!state.Size);
        if (complete2 && !findVariant()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          toast('That colour and size combination is unavailable');
        }
      }, true);
    }

    // sticky "choose your size" → scroll to size block
    var stickyBtn = document.querySelector('[data-ev-sticky-atc] [data-ev-sticky-action]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (!kachingActive() && hasSize && !state.Size) {
          var sizeBlock = root.querySelector('[data-ev-size-block]');
          if (sizeBlock) sizeBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
          toast('Please choose your size');
          return;
        }
        // trigger the real ATC (live availability handled by /cart/add.js)
        var realForm = root.querySelector('[data-ev-cart-form]');
        // old Safari has no requestSubmit → call the AJAX add directly; native .submit() would skip
        // our submit handler and full-page-reload to /cart instead of opening the drawer
        if (realForm) { if (realForm.requestSubmit) { realForm.requestSubmit(); } else { cartAdd(realForm); } }
      });
    }

    // zoom: keep current tag/caption in sync
    var zoom = document.getElementById('ev-zoom');
    if (zoom) {
      root.querySelectorAll('[data-ev-zoom-open]').forEach(function (z) {
        z.addEventListener('click', function () {
          if (root._galSwiped) { root._galSwiped = false; return; } // ignore the click that follows a swipe
          var zt = zoom.querySelector('[data-ev-zoom-tag]');
          var zc = zoom.querySelector('[data-ev-zoom-caption]');
          var zi = zoom.querySelector('[data-ev-zoom-image]');
          if (zt) zt.textContent = root._zoomTag || (mainTag ? mainTag.textContent : '');
          if (zc) zc.textContent = root._zoomCap || (mainCap ? mainCap.textContent : '');
          if (zi && mainImg && mainImg.src) {
            zi.src = mainImg.src;
            if (mainImg.srcset) { zi.srcset = mainImg.srcset; } else { zi.removeAttribute('srcset'); }
            zi.removeAttribute('hidden'); // markup ships hidden so the empty <img> never flashes
          }
          zoom.style.setProperty('--ev-gallery-grad', grads[state.Color] || '');
          openOverlay('ev-zoom');
        });
      });
      // desktop grid tiles -> open zoom with that tile's image
      root.querySelectorAll('[data-ev-galtile]').forEach(function (tile) {
        tile.addEventListener('click', function () {
          var zi = zoom.querySelector('[data-ev-zoom-image]');
          var src = tile.getAttribute('data-image');
          if (zi && src) {
            zi.src = src;
            var ss = tile.getAttribute('data-srcset');
            if (ss) { zi.srcset = ss; } else { zi.removeAttribute('srcset'); }
            zi.removeAttribute('hidden');
          }
          var zt = zoom.querySelector('[data-ev-zoom-tag]'); if (zt) zt.textContent = '';
          var zc = zoom.querySelector('[data-ev-zoom-caption]'); if (zc) zc.textContent = '';
          zoom.style.setProperty('--ev-gallery-grad', grads[state.Color] || '');
          openOverlay('ev-zoom');
        });
      });
    }

    retint();
    // Safety net: if the default colour rendered an empty gallery (its media isn't tagged —
    // possible after an alt-tag drift), reveal + hydrate all media so it's never blank.
    if (!root.querySelector('[data-ev-galtile] img, [data-ev-thumb] img')) {
      hydrateAll();
      root.querySelectorAll('[data-ev-galtile], [data-ev-thumb]').forEach(function (t) { t.style.display = ''; });
    }
    updateAtc();

    // Kaching Bundles mounts its UI asynchronously (after our init). When its block
    // appears/disappears, recompute the ATC so it flips to a clickable "Add to cart"
    // (Kaching carries its own size/colour) instead of staying on "Choose your size".
    (function watchKaching() {
      var last = kachingActive();
      var rerun = function () {
        var now = kachingActive();
        if (now !== last) { last = now; updateAtc(); }
      };
      if ('MutationObserver' in window) {
        var mo = new MutationObserver(rerun);
        mo.observe(document.body, { childList: true, subtree: true });
      }
      // Belt-and-braces: poll briefly in case the app mounts in a way the observer misses.
      var ticks = 0;
      var iv = setInterval(function () { rerun(); if (++ticks >= 30) clearInterval(iv); }, 200);
    })();
  }

  /* ---------- helpers ---------- */
  function readJson(el) { if (!el) return null; try { return JSON.parse(el.textContent); } catch (e) { return null; } }
  function formatMoney(cents, fmt) {
    var v = (cents / 100);
    var withDecimals = v.toFixed(2);
    var noDecimals = Math.round(v).toString();
    // group thousands for the *_with_comma_separator placeholders
    function group(n) { return n.replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    var amount = withDecimals; // {{amount}} always shows 2 decimals, matching Shopify's money filter
    return (fmt || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, group(noDecimals))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, group(withDecimals))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  /* ---------- expose + boot ---------- */
  window.EVERENZA = {
    openOverlay: openOverlay, closeOverlay: closeOverlay, closeAllOverlays: closeAllOverlays,
    toast: toast, cartAdd: cartAdd, refreshCart: refreshCart, cartChange: cartChange
  };

  // begin_checkout on any checkout button (does not block navigation)
  document.addEventListener('click', function (e) {
    var co = e.target.closest('[name="checkout"], [data-ev-checkout]');
    if (co) track('begin_checkout', {}, 'InitiateCheckout', {});
  });

  // view_item on PDP
  function trackViewItem() {
    var root = document.querySelector('[data-ev-pdp]');
    if (!root) return;
    var name = (root.querySelector('.ev-pdp__title') || {}).textContent;
    track('view_item', { ecommerce: { items: [{ item_name: name }] } }, 'ViewContent', { content_name: name });
  }

  function boot() {
    initPdp(); // core interactivity (gallery, variants, cart form) — keep immediate
    // sticky ATC + analytics aren't needed for first paint/interaction → defer off the
    // critical init task to cut main-thread blocking (helps INP/TBT on mobile).
    var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 1); };
    idle(function () { initStickyAtc(); trackViewItem(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
